import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Check } from 'lucide-react'
import type { ScenarioMode } from '@/types/api'
import { cn } from '@/lib/utils'

const MODES: Array<{
  id: ScenarioMode
  name: string
  description: string
  tooltip: string
}> = [
  {
    id: 'HISTORICAL_REPLAY',
    name: 'Historical Replay',
    description: 'Apply observed historical market moves to your current portfolio',
    tooltip: 'Select historical episodes like "COVID Mar 2020" or "2021-22 Rate Hike Cycle" and see how your portfolio would have performed during that period.',
  },
  {
    id: 'BTC_CURVE_REGIME',
    name: 'BTC Curve + Regime',
    description: 'Input a BTC price path and select a TradFi regime template',
    tooltip: 'Crypto assets are priced based on your BTC path and beta mode. TradFi assets use regime template assumptions (equity, rates, credit). Independent pricing.',
  },
  {
    id: 'CUSTOM_SHOCKS',
    name: 'Custom Shocks',
    description: 'Specify explicit percentage moves for individual assets',
    tooltip: 'Directly input shock percentages per asset or factor. Coherence checker still validates statistical plausibility.',
  },
]

interface ModeSelectorProps {
  value: ScenarioMode
  onChange: (mode: ScenarioMode) => void
}

export function ModeSelector({ value, onChange }: ModeSelectorProps) {
  return (
    <TooltipProvider>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {MODES.map((mode) => {
          const isSelected = value === mode.id
          
          return (
            <Tooltip key={mode.id}>
              <TooltipTrigger asChild>
                <Card
                  className={cn(
                    "cursor-pointer transition-all hover:shadow-md",
                    isSelected
                      ? 'border-primary bg-primary/5 ring-2 ring-primary shadow-sm'
                      : 'border-border hover:border-primary/50'
                  )}
                  onClick={() => onChange(mode.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base font-semibold">{mode.name}</CardTitle>
                      {isSelected && (
                        <div className="rounded-full bg-primary p-1 text-primary-foreground">
                          <Check className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed">
                      {mode.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-sm">
                <p className="text-sm">{mode.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
    </TooltipProvider>
  )
}
